import React, { useState } from 'react';
import { View, ScrollView, Pressable, StyleSheet, Alert, ActivityIndicator } from 'react-native';
import { Text, TextInput } from '../ui/Text';
import type { NativeStackScreenProps } from '@react-navigation/native-stack';
import { useKnownAgents, useRooms, createRoom, createSpace, inviteByEmail, addRoomToSpace, sendText, toMxid, localpart, agentHomeRoom, startThread } from '../matrix';
import { Btn } from '../ui/Btn';
import { KeyboardShift } from '../ui/KeyboardShift';
import { Avatar } from '../ui/Avatar';
import { UserPicker } from '../ui/UserPicker';
import { radius, makeStyles, useTheme } from '../theme';
import type { RootStackParamList } from '../navTypes';

type Props = NativeStackScreenProps<RootStackParamList, 'NewChat'>;

export default function NewChatScreen({ navigation }: Props) {
  const theme = useTheme(); const s = useStyles();
  const agents = useKnownAgents();
  const rooms = useRooms();
  const [space, setSpace] = useState('');
  const [busy, setBusy] = useState<string | null>(null);

  // Each "new conversation" is a separate session; stamp date + time so two on the same day stay distinguishable.
  const now = new Date();
  const today = `${now.toLocaleDateString([], { month: 'short', day: 'numeric' })}, ${now.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' })}`;

  const go = (roomId: string, title: string) => navigation.replace('Chat', { roomId, title });

  /** "New conversation" with an agent = a new thread in the agent's room (Hermes keeps a separate memory per
   *  thread), titled with the time. Only if the agent has no room yet do we fall back to creating one. */
  const startWithAgent = (id: string, name: string) => {
    const title = `${name} · ${today}`;
    const homeRoom = agentHomeRoom(id);
    if (homeRoom) {
      setBusy(id);
      startThread(homeRoom.id, title)
        .then((rootId) => navigation.replace('Thread', { roomId: homeRoom.id, rootId }))
        .catch((e) => Alert.alert('Could not start conversation', e?.message))
        .finally(() => setBusy(null));
      return;
    }
    const home = rooms.find((sp) => sp.isSpace && sp.children.some((cid) => rooms.find((r) => r.id === cid)?.members[id]));
    setBusy(id);
    createRoom({ name: title, invite: [id] })
      .then(async (rid) => { if (home) await addRoomToSpace(home.id, rid).catch(() => {}); return rid; })
      .then((rid) => {
        // Open the conversation so it never looks empty. The agent auto-joins within ~1s of the
        // invite; give it a moment so it sees the message as a live event, not history.
        setTimeout(() => { sendText(rid, 'Hi — starting a new conversation.').catch(() => {}); }, 1500);
        return go(rid, title);
      })
      .catch((e) => Alert.alert('Could not start conversation', e?.message))
      .finally(() => setBusy(null));
  };

  /** Direct message with a person (or an agent) by username. */
  /** A chat with someone who has no account yet: a room named after them, and an email invite that lands them in it. */
  const startDmByEmail = (email: string) => {
    setBusy('dm');
    createRoom({ name: email, isDirect: true })
      .then(async (rid) => { await inviteByEmail(rid, email); return rid; })
      .then((rid) => { Alert.alert('Invitation sent', `${email} gets an email with a sign-up link and lands in this chat once they sign up.`); go(rid, email); })
      .catch((e) => Alert.alert('Could not invite', e?.message))
      .finally(() => setBusy(null));
  };
  const startDm = (mxid: string, title: string) => {
    setBusy('dm');
    createRoom({ invite: [mxid], isDirect: true })
      .then((rid) => go(rid, title))
      .catch((e) => Alert.alert('Could not start chat', e?.message))
      .finally(() => setBusy(null));
  };

  const makeSpace = () => {
    const n = space.trim(); if (!n) return;
    setBusy('space');
    createSpace(n)
      .then(() => { setSpace(''); Alert.alert('Workspace created', `"${n}" is ready. Add rooms to it from a room's info screen.`); navigation.goBack(); })
      .catch((e) => Alert.alert('Could not create workspace', e?.message))
      .finally(() => setBusy(null));
  };

  return (
    <KeyboardShift>
    <ScrollView style={s.root} contentContainerStyle={{ padding: 16, paddingBottom: 40 }} keyboardShouldPersistTaps="handled">
      <Text style={s.h}>New conversation with an agent</Text>
      <Text style={s.hint}>Pick an agent. Each conversation gets its own thread in the agent's room, so the agent starts fresh.</Text>
      {agents.length === 0 ? <Text style={s.empty}>No agents found yet — they appear once you're in an agent room.</Text> : agents.map((a) => (
        <Pressable key={a.id} onPress={() => startWithAgent(a.id, a.name)} disabled={!!busy}
          style={({ pressed }) => [s.row, pressed && { backgroundColor: theme.surface2 }]}>
          <Avatar name={a.name} kind="agent" size={40} />
          <View style={{ flex: 1, minWidth: 0 }}>
            <Text style={s.name} numberOfLines={1}>{a.name}</Text>
            <Text style={s.id} numberOfLines={1}>Agent · @{localpart(a.id)}</Text>
          </View>
          {busy === a.id ? <ActivityIndicator color={theme.accent} /> : <Text style={s.chev}>›</Text>}
        </Pressable>
      ))}

      <Text style={[s.h, { marginTop: 24 }]}>Message someone</Text>
      <Text style={s.hint}>A direct message with a person or agent. Search by name and pick them from the list.</Text>
      <UserPicker confirmLabel="Start chat" busy={busy === 'dm'} onPick={(u) => startDm(u.id, u.name)} onPickEmail={startDmByEmail} />

      <Text style={[s.h, { marginTop: 24 }]}>New workspace</Text>
      <Text style={s.hint}>A workspace groups rooms for a team or a customer. Invite people to it, or add rooms from a room's info screen.</Text>
      <View style={{ flexDirection: 'row', gap: 8 }}>
        <TextInput style={[s.input, { flex: 1 }]} value={space} onChangeText={setSpace} placeholder="Workspace name"
          placeholderTextColor={theme.faint} onSubmitEditing={makeSpace} />
        <Btn label="Create" onPress={makeSpace} busy={busy === 'space'} />
      </View>
    </ScrollView>
    </KeyboardShift>
  );
}


const useStyles = makeStyles((theme) => StyleSheet.create({
  root: { flex: 1, backgroundColor: theme.bg },
  h: { color: theme.faint, fontSize: 11, fontWeight: '800', letterSpacing: 0.8, textTransform: 'uppercase', marginBottom: 4, fontFamily: theme.mono },
  hint: { color: theme.muted, fontSize: 13, marginBottom: 10, lineHeight: 18 },
  empty: { color: theme.faint, fontSize: 13, paddingVertical: 8 },
  row: { flexDirection: 'row', alignItems: 'center', gap: 12, padding: 12, borderRadius: radius.md, backgroundColor: theme.surface, borderWidth: 1, borderColor: theme.border, marginBottom: 8 },
  name: { color: theme.ink, fontSize: 16, fontWeight: '600' },
  id: { color: theme.faint, fontSize: 12, marginTop: 2, fontFamily: theme.mono },
  chev: { color: theme.faint, fontSize: 22 },
  input: { backgroundColor: theme.surface, borderWidth: 1, borderColor: theme.border, borderRadius: radius.md, color: theme.ink, fontSize: 15, paddingHorizontal: 12, paddingVertical: 10 },
}));
